import {v4} from 'uuid'
import {action, makeObservable, observable} from 'mobx'
import {defaultSchedule, RateSchedule, removeScheduleValue, Schedule, ScheduleRate, shiftScheduleValues} from 'components/project/model/Schedule'
import {TimeSeries} from 'components/project/model/TimeSeries'
import {MultiYearVar} from 'components/project/model/MultiYearVar'
import {getColor} from 'colors'

export type ContractEnergyPriceKind = 'schedule' | 'import'
export type ContractObligationKind = 'daily' | 'import' | 'none'

// rate color selection from palette
let nextRateColorIndex = 0

export function getNextRateColor() {
    const rv = getColor(nextRateColorIndex)
    nextRateColorIndex += 2
    return rv
}

export type DayKind = 'all' | 'weekday' | 'weekend'

export class ContractObligationRequirement {
    id: string = v4()
    constructor(init: Partial<ContractObligationRequirement> = {}) {
        Object.assign(this, init)
        makeObservable(this)
    }

    @observable dayKind: DayKind = 'all'
    @observable startDate: Date = new Date()
    @observable endDate: Date = new Date()
    @observable startHour: number = 0 // 0..23
    @observable endHour: number = 1 // 1..24
    @observable value: number = 0 // (MWh)
    @observable obligationTimeSeries: TimeSeries = new TimeSeries()

    @action setDayKind(x: DayKind) { this.dayKind = x }
    @action setStartDate(x: Date) { this.startDate = x}
    @action setStartHour(x: number) { this.startHour = x }
    @action setEndDate(x: Date) { this.endDate = x }
    @action setEndHour(x: number) { this.endHour = x }
    @action setValue(x: number) { this.value = x }
}

export class Contract {
    id: string = v4()
    constructor(lifetime: number) {
        makeObservable(this)
        this.energyPriceEscalator.updateLifetime(lifetime)
    }

    // energy price
    @observable energyPriceKind: ContractEnergyPriceKind = 'schedule'

    // for kind = 'schedule'
    //@observable energyPriceRates: ScheduleRate[] = observable([])
    //@observable energyPriceSchedule: Schedule = defaultSchedule()
    @observable energyPriceSchedule: RateSchedule = new RateSchedule

    // for kind = 'import'
    @observable energyPriceTimeSeries: TimeSeries = new TimeSeries()

    @observable energyPriceEscalator: MultiYearVar = new MultiYearVar()

    // obligations
    @observable obligationKind: ContractObligationKind = 'daily'

    // for kind = 'daily'
    @observable obligations: ContractObligationRequirement[] = observable([])
    @observable dailyObligationsTimeSeries: TimeSeries = new TimeSeries()

    // for kind = 'import'
    @observable obligationTimeSeries: TimeSeries = new TimeSeries()

    // for kind = 'none'
    @observable contractedSolarCapacity: number = 100
    @observable contractedWindCapacity: number = 100
    @observable contractedStorageCapacity: number = 100
    @observable competeWithWholesaleMarket: boolean = false

    @action setScheduleData(data: number[], year: number) {
        this.energyPriceTimeSeries.setData(data, year)
    }

    @action setObligationData(data: number[], year: number) {
        this.obligationTimeSeries.setData(data, year)
    }

    @action setEnergyPriceKind(x: ContractEnergyPriceKind) { this.energyPriceKind = x }
    @action setObligationKind(x: ContractObligationKind) { this.obligationKind = x }

    @action addNewObligation(init: Partial<ContractObligationRequirement>, isEdit?: boolean) {
        const x = new ContractObligationRequirement(init)
        if (!isEdit)
            this.obligations.push(x)
        else {
            const index = this.obligations.findIndex(i => i.id === x.id)
            this.obligations[index] = x;
        }
    }

    @action removeObligation(id: string) {
        const index = this.obligations.findIndex(x => x.id === id)
        this.obligations.splice(index, 1)
    }

    @action setContractedSolarCapacity(x: number) { this.contractedSolarCapacity = x }
    @action setContractedWindCapacity(x: number) { this.contractedWindCapacity = x }
    @action setContractedStorageCapacity(x: number) { this.contractedStorageCapacity = x }
    @action setCompeteWithWholesaleMarket(x: boolean) { this.competeWithWholesaleMarket = x }
}
